import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

import type { ReactNode } from "react";

import { appApi } from "api/app-api";

import { useAuth } from "./AuthContext";

import type { Badges } from "models/app.model";

// Sidenav counters and the server clock. They refresh on their own schedule, so they
// live apart from everything else: a refresh touches the sidenav, nothing more.
type BadgesValue = {
  badges: Badges | null;
  time: number | null;
  refreshBadges: () => void;
};

const BadgesContext = createContext<BadgesValue | null>(null);

BadgesContext.displayName = "BadgesContext";

function BadgesProvider({ children }: { children: ReactNode }) {
  const { isAuthorized } = useAuth();
  const [badges, setBadges] = useState<Badges | null>(null);
  const [time, setTime] = useState<number | null>(null);

  // Used to be `setUpadeBadges(true)` plus an effect watching the flag, which fired a
  // second request whenever the flag was reset. A plain call does the same job.
  const refreshBadges = useCallback(() => {
    appApi.getBadges().then((info) => {
      if (!info) return;

      const { st, ...counters } = info;

      setTime(st ?? null);
      setBadges(counters);
    });
  }, []);

  useEffect(() => {
    if (!isAuthorized) {
      setBadges(null);
      setTime(null);

      return;
    }

    refreshBadges();
  }, [isAuthorized, refreshBadges]);

  const value = useMemo(
    () => ({
      badges: badges,
      time: time,
      refreshBadges: refreshBadges,
    }),
    [badges, time, refreshBadges]
  );

  return <BadgesContext.Provider value={value}>{children}</BadgesContext.Provider>;
}

function useBadges(): BadgesValue {
  const context = useContext(BadgesContext);

  if (!context) {
    throw new Error("useBadges must be used inside AppProvider.");
  }

  return context;
}

export { BadgesProvider, useBadges };
